import {
	Box,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography
} from "@mui/material";
import React from "react";
import clsx from "clsx";
import { useTranslation } from 'react-i18next';
import { IRubricInfo } from "@repositories/model/transfer/IRubricInfo";
import { RubricItemRow } from './RubricItemRow';
import { rubricTableStyles } from './RubricTable.styles';

type RubricTableVariant = 
	| 'student-result'      // 학생 결과 조회용 (4컬럼)
	| 'scoring'             // 채점용 (2컬럼, 평가항목/평가기준)
	| 'export'              // 내보내기용 (4컬럼, 다중 루브릭)
	| 'shared'              // 공유 탭용 (2컬럼, 점수 포함)
	| 'confirm';            // 확인용 (4컬럼)

type Props = {
	rubricInfo?: IRubricInfo;      // 단일 루브릭
	rubrics?: IRubricInfo[];        // 다중 루브릭 (export용)
	variant?: RubricTableVariant;
	className?: string;
	classes?: any;
}

export const RubricTable = (props: Props) => {
	const { rubricInfo, rubrics, variant = 'student-result', className, classes } = props;
	const { t } = useTranslation();

	// 다중 루브릭 처리 (export 버전)
	if (variant === 'export' && rubrics) {
		return (
			<TableContainer 
				component={Paper} 
				className={clsx(classes?.tableBox2, classes?.tableBoxMarginTop, className)}
				sx={{
					...rubricTableStyles.tableContainer,
					...rubricTableStyles.exportTable,
					...rubricTableStyles.fadeIn,
					...rubricTableStyles.responsive,
				}}
			>
				<Table aria-label="simple table">
					<TableHead sx={rubricTableStyles.tableHead}>
						<TableRow>
							<TableCell width='25%' align="center">{t("평가항목")}</TableCell>
							<TableCell width='20%' align="center" colSpan={2} className={classes?.scoreCell}>{t("배점")}</TableCell>
							<TableCell width='55%' align="center">{t("평가 기준")}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody sx={rubricTableStyles.tableBody}>
						{rubrics.map((rubric, idx) => (
							<React.Fragment key={idx}>
								<TableRow className="rubric-group">
									<TableCell 
										width='17%' 
										rowSpan={rubric.items?.length + 1 || 0} 
										align="center" 
										className={classes?.rubricCellBgColor}
										sx={rubricTableStyles.rubricNameCell}
									>
										<Box display='flex' flexDirection='column' alignItems='center'>
											<Typography>{rubric.name}</Typography>
										</Box>
									</TableCell>
								</TableRow>
								{rubric?.items.length > 0 &&
									rubric.items.map((item, itemIdx) => (
										<RubricItemRow 
											key={itemIdx} 
											itemInfo={item}
											variant="default"
											classes={classes}
										/>
									))}
							</React.Fragment>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		);
	}

	// 단일 루브릭이 없으면 빈 Fragment 반환
	if (!rubricInfo) {
		return <></>;
	}

	// 채점용 (2컬럼)
	if (variant === 'scoring') {
		return (
			<TableContainer 
				component={Paper} 
				className={clsx(classes?.tableBox, classes?.rubricTable, className)}
				sx={{
					...rubricTableStyles.tableContainer,
					...rubricTableStyles.fadeIn,
					...rubricTableStyles.responsive,
				}}
			>
				<Table aria-label="simple table">
					<TableHead sx={rubricTableStyles.tableHead}>
						<TableRow>
							<TableCell width='26%' align="center" sx={{ borderRight: '1px solid rgba(255, 255, 255, 0.1)' }}>{t("평가항목")}</TableCell>
							<TableCell width='74%' align="center">{t("평가 기준")}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody sx={rubricTableStyles.tableBody}>
						<TableRow>
							<TableCell 
								width='17%' 
								rowSpan={rubricInfo.items?.length + 1 || 0} 
								align="center"
								sx={rubricTableStyles.rubricNameCell}
							>
								<Box display='flex' flexDirection='column' alignItems='center'>
									<Typography>{t(rubricInfo.name)}</Typography>
								</Box>
							</TableCell>
						</TableRow>
						{rubricInfo?.items.length > 0 &&
							rubricInfo.items.map((item, idx) => (
								<RubricItemRow
									key={idx}
									itemInfo={item}
									variant="scoring"
									classes={classes}
								/>
							))}
					</TableBody>
				</Table>
			</TableContainer>
		);
	}

	// shared 버전 (2컬럼 + 점수)
	if (variant === 'shared') {
		return (
			<Box sx={{ margin: '20px 0px 10px' }}>
				<TableContainer 
					component={Paper} 
					className={clsx(classes?.evaluationTableBox, className)}
					sx={{
						...rubricTableStyles.tableContainer,
						...rubricTableStyles.fadeIn,
						...rubricTableStyles.responsive,
					}}
				>
				<Table aria-label="simple table">
					<TableHead sx={rubricTableStyles.tableHead}>
						<TableRow>
							<TableCell width='30%' align="center" className={classes?.bgLeftBox}>{t('평가항목')}</TableCell>
							<TableCell width='70%' align="center" colSpan={3}>{t('평가 기준')}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody sx={rubricTableStyles.tableBody}>
						<TableRow>
							<TableCell 
								width='15%' 
								rowSpan={rubricInfo?.items?.length + 1 || 0} 
								align="center" 
								className={classes?.bgLeftBox}
								sx={rubricTableStyles.rubricNameCell}
							>
								<Typography className={classes?.rubricNameItem}>
									{rubricInfo.name}
								</Typography>
								<Box sx={rubricTableStyles.scoreBadge}>
									{rubricInfo.score}{t('점')}
								</Box>
							</TableCell>
						</TableRow>
						{rubricInfo.items.length > 0 &&
							rubricInfo.items.map((item, idx) => (
								<TableRow key={idx}>
									<TableCell width='10%' align="center">
										<Typography className={classes?.fontScoreName}>
											{item.name}
										</Typography>
									</TableCell>
									<TableCell width='10%' align="center">
										<Typography className={classes?.scoreMark} sx={rubricTableStyles.scoreText}>
											<span>{item.score}</span>
											{t('점')}
										</Typography>
									</TableCell>
									<TableCell width='80%'>
										<Typography className={classes?.fontScoreStandard}>
											{item.standard}
										</Typography>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>
		);
	}

	// confirm 버전 (4컬럼)
	if (variant === 'confirm') {
		return (
			<Box sx={{ margin: '20px 0px 10px' }}>
				<TableContainer 
					component={Paper} 
					className={clsx(classes?.evaluationTableBox, className)}
					sx={{
						...rubricTableStyles.tableContainer,
						...rubricTableStyles.fadeIn,
						...rubricTableStyles.responsive,
					}}
				>
					<Table aria-label="simple table">
						<TableHead sx={rubricTableStyles.tableHead}>
							<TableRow>
								<TableCell width='11%' align="center" className={classes?.bgLeftBox}>
									{t("평가항목")}
								</TableCell>
								<TableCell width='16%' colSpan={2} align="center" className={classes?.bgLeftBox}>
									{t("배점")}
								</TableCell>
								<TableCell width='73%' align="center">
									{t("평가 기준")}
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody sx={rubricTableStyles.tableBody}>
							<TableRow>
								<TableCell 
									width='28%' 
									rowSpan={rubricInfo?.items?.length + 1 || 0} 
									align="center" 
									className={classes?.bgLeftBox}
									sx={rubricTableStyles.rubricNameCell}
								>
									<Typography className={classes?.scoreMark1}>{rubricInfo.name}</Typography>
									<Typography className={classes?.scoreMark1} sx={rubricTableStyles.scoreText}>
										<span>{rubricInfo.score}</span>{t("점")}
									</Typography>
								</TableCell>
							</TableRow>
							{rubricInfo.items.length > 0 &&
								rubricInfo.items.map((item, idx) => (
									<TableRow 
										key={idx}
										sx={{ '&:last-child .MuiTableCell-body': { border: '0px !important' } }}
									>
										<TableCell align="center" className={classes?.evaluationTableBgColor} sx={rubricTableStyles.itemNameCell}>
											<Typography className={classes?.fontScoreName}>{item.name}</Typography>
										</TableCell>
										<TableCell align="center" className={classes?.evaluationTableBgColor} sx={rubricTableStyles.itemNameCell}>
											<Typography className={classes?.scoreMark1} sx={rubricTableStyles.scoreText}>
												<span>{item.score}</span>{t("점")}
											</Typography>
										</TableCell>
										<TableCell className={classes?.borderLine} sx={rubricTableStyles.standardCell}>
											<Box className={clsx(classes?.scoreMarkInput, classes?.scoreMarkInputMulti)}>
												<Typography className={classes?.fontScoreStandard}>{item.standard}</Typography>
											</Box>
										</TableCell>
									</TableRow>
								))}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>
		);
	}

	// 기본 버전 (student-result: 4컬럼)
	return (
		<TableContainer 
			component={Paper} 
			className={clsx(classes?.tableBox2, className)}
			sx={{
				...rubricTableStyles.tableContainer,
				...rubricTableStyles.fadeIn,
				...rubricTableStyles.responsive,
			}}
		>
			<Table aria-label="simple table">
				<TableHead sx={rubricTableStyles.tableHead}>
					<TableRow>
						<TableCell width='25%' align="center">{t("평가항목")}</TableCell>
						<TableCell width='10%'></TableCell>
						<TableCell width='10%'>{t("배점")}</TableCell>
						<TableCell width='65%' align="center">{t("평가 기준")}</TableCell>
					</TableRow>
				</TableHead>
				<TableBody sx={rubricTableStyles.tableBody}>
					<TableRow>
						<TableCell 
							width='17%' 
							rowSpan={rubricInfo.items?.length + 1 || 0} 
							align="center"
							sx={rubricTableStyles.rubricNameCell}
						>
							<Box display='flex' flexDirection='column' alignItems='center'>
								<Typography>{rubricInfo.name}</Typography>
							</Box>
						</TableCell>
					</TableRow>
					{rubricInfo?.items.length > 0 &&
						rubricInfo.items.map((item, idx) => (
							<RubricItemRow
								key={idx}
								itemInfo={item}
								variant="default"
								classes={classes}
							/>
						))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
